import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Select from 'react-select';
import { AutomateError, deleteReadNotifications, markNotificationsAsRead } from '../api';
import { useApp } from '../common/AppProvider';
import TextWithArrows from '../common/TextWithArrows';
import { Notification } from '../tasks/types';
import { translations } from '../translations/translations';
import { ExButton } from '@boomi/exosphere';
import ItemList from '../common/ItemList';

import './notifications.css';

const splitNotificationText = (notificationText: string): string[] => {
    return notificationText.split(/:(.*)/s);
};

type TaskFilterOption = { label: string | undefined; value: string | undefined };

const Notifications = () => {
    const { language, tasks, notifications, setNotifications, alertError, setIsLoading } = useApp();

    const [selectedTask, setSelectedTask] = useState<TaskFilterOption | null>(null);
    const [displayNotifications, setDisplayNotifications] = useState<Notification[]>(notifications);

    const [searchParams] = useSearchParams();

    const sortByDate = (notifications: Notification[]) => {
        return notifications.sort((a, b) => {
            const dateA = new Date(a.createdAt);
            const dateB = new Date(b.createdAt);

            return dateB.valueOf() - dateA.valueOf();
        });
    };

    useEffect(() => {
        const taskId = searchParams.get('taskId');
        if (taskId) {
            const task = tasks.find((t) => t.id === taskId);
            if (task) {
                setSelectedTask({ value: taskId, label: task.displayName });
            }
        }
    }, [searchParams, tasks]);

    useEffect(() => {
        if (!selectedTask) {
            setDisplayNotifications(sortByDate(notifications));
        } else {
            const filteredNotifications = notifications.filter(
                (notification) => notification.taskId === selectedTask.value,
            );
            setDisplayNotifications(sortByDate(filteredNotifications));
        }
    }, [selectedTask, notifications]);

    const getUnreadNotifications = () => {
        const unreadNotificationIds: Array<string> = [];
        notifications.forEach((element) => {
            if (!element.read) {
                unreadNotificationIds.push(element.id);
            }
        });

        return unreadNotificationIds;
    };

    const readAllNotifications = async () => {
        const unreadNotificationIds: string[] = getUnreadNotifications();
        if (unreadNotificationIds.length === 0) return;

        setIsLoading(true);

        await markNotificationsAsRead(unreadNotificationIds)
            .then(() => {
                notifications.forEach((_, index) => {
                    notifications[index].read = true;
                });
                setNotifications([...notifications]);
            })
            .catch((e: AutomateError) =>
                alertError({
                    message: translations[language].ERROR_reading_all_notifications,
                    traceId: e.json?.traceId,
                }),
            )
            .finally(() => setIsLoading(false));
    };

    const deleteAllReadNotifications = async () => {
        setIsLoading(true);

        await deleteReadNotifications()
            .then(() =>
                setNotifications(
                    notifications.filter((element) => {
                        return !element.read;
                    }),
                ),
            )
            .catch((e: AutomateError) =>
                alertError({
                    message: translations[language].ERROR_deleting_read_notifications,
                    traceId: e.json?.traceId,
                }),
            )
            .finally(() => setIsLoading(false));
    };

    const readSingleMessage = async (notification: Notification) => {
        setIsLoading(true);

        await markNotificationsAsRead([notification.id])
            .then(() => {
                notifications.forEach((element, index) => {
                    if (element.id === notification.id) {
                        notifications[index].read = true;
                    }
                });
                setNotifications([...notifications]);
            })
            .catch((e: AutomateError) =>
                alertError({
                    message: translations[language].ERROR_reading_single_notification,
                    traceId: e.json?.traceId,
                }),
            )
            .finally(() => setIsLoading(false));
    };

    const getFilterOptions = () => {
        // We only need the notifications that are linked with tasks and no duplicates. Also filter out the current selected task.
        const filteredNotifications = notifications.filter(
            (notification, index, self) =>
                notification.taskId !== undefined &&
                index === self.findIndex((t) => t.taskId === notification.taskId) &&
                notification.taskId !== selectedTask?.value,
        );

        return filteredNotifications.map((notification) => {
            return {
                label: tasks.find((task) => task.id === notification.taskId)?.displayName,
                value: notification.taskId,
            };
        });
    };

    return (
        <ItemList
            title={translations[language].USER_NOTIFICATIONS_heading}
            toolbarFilters={
                <Select
                    className="react-select-container"
                    classNamePrefix="react-select"
                    options={getFilterOptions()}
                    onChange={setSelectedTask}
                    placeholder={translations[language].USER_NOTIFICATIONS_select_placeholder}
                    value={selectedTask}
                    menuPortalTarget={document.querySelector<HTMLElement>('.automate-root')}
                    menuPosition="fixed"
                    menuPlacement="auto"
                    isMulti={false}
                    isClearable={true}
                />
            }
            toolbarActions={
                <>
                    <ExButton
                        className="exo-button"
                        data-testId="confirmCancelButton"
                        flavor="base"
                        type="secondary"
                        size="large"
                        disabled={getUnreadNotifications().length === 0}
                        onClick={readAllNotifications}
                    >
                        {translations[language].USER_NOTIFICATIONS_all_read}
                    </ExButton>

                    <ExButton
                        className="exo-button"
                        data-testId="confirmDeleteRead"
                        flavor="risky"
                        type="primary"
                        size="large"
                        disabled={notifications.length === 0}
                        onClick={deleteAllReadNotifications}
                    >
                        {translations[language].USER_NOTIFICATIONS_delete_read}
                    </ExButton>
                </>
            }
            items={displayNotifications}
            itemContent={(notification) => {
                const task = tasks.find((task) => task.id === notification.taskId);

                return (
                    <li key={notification.id}>
                        <button
                            className="user-notification-layout ta-tile ta-tile-interactive"
                            onClick={() => {
                                if (!notification.read) {
                                    void readSingleMessage(notification);
                                }
                            }}
                        >
                            <span
                                className={`read-indicator ${
                                    !notification.read ? 'is-unread' : ''
                                }`}
                            ></span>
                            <span className="user-notification-message title md">
                                {!!task && <TextWithArrows text={`${task.displayName}: `} />}
                                <TextWithArrows
                                    text={splitNotificationText(notification.message)[0]}
                                />
                            </span>
                            <span className="user-notification-details text md">
                                {splitNotificationText(notification.message)[1]}
                            </span>
                            <span className="user-notification-date text md">
                                {new Date(notification.createdAt).toLocaleString(
                                    navigator.language,
                                )}
                            </span>
                        </button>
                    </li>
                );
            }}
        />
    );
};

export default Notifications;
